import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api, STATUS_TO_PILL } from "@/lib/api";
import { toast } from "sonner";
import { Truck } from "lucide-react";

const fetcher = (u) => api.get(u).then(r=>r.data);

const F = ({label, children}) => <div><label className="text-[11px] uppercase font-semibold text-slate-500">{label}</label>{children}</div>;

export function GRNPage() {
  const { data: grns } = useSWR("/grn", fetcher);
  const { data: suppliers } = useSWR("/suppliers", fetcher);
  const { data: materials } = useSWR("/raw_materials", fetcher);
  const { data: warehouses } = useSWR("/warehouses", fetcher);
  const { data: ppos } = useSWR("/purchase-orders", fetcher);
  const [show, setShow] = useState(false);
  const [fromPO, setFromPO] = useState(null);
  const [f, setF] = useState({ warehouse_id: "wh_rm", uom: "kg" });

  const openPOs = (ppos||[]).filter(p => ["SENT","PARTIALLY_RECEIVED"].includes(p.status));
  const mMap = Object.fromEntries((materials||[]).map(m=>[m.material_id,m]));
  const sMap = Object.fromEntries((suppliers||[]).map(s=>[s.supplier_id,s]));

  const submit = async () => {
    try { await api.post("/grn", f); toast.success("GRN posted (Quarantine)"); setShow(false); setF({warehouse_id:"wh_rm",uom:"kg"}); mutate("/grn"); }
    catch(e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const submitFromPO = async () => {
    try {
      const lines = fromPO.items.filter(it=>it.receive_qty>0).map(it=>({
        material_id: it.material_id, qty: it.receive_qty, rate: it.rate, uom: it.uom||"kg", batch: it.batch||""
      }));
      if (lines.length===0) return toast.error("Enter quantities to receive");
      const r = await api.post(`/grn/from-po/${fromPO.ppo_id}`, { lines, warehouse_id: fromPO.warehouse_id||"wh_rm" });
      toast.success(`${r.data.grns.length} GRN(s) posted · PO now ${r.data.ppo_status}`);
      setFromPO(null); mutate("/grn"); mutate("/purchase-orders");
    } catch(e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const openFromPO = (po) => {
    const remaining = po.items.map(it => {
      const recvd = (grns||[]).filter(g => g.ppo_id === po.ppo_id && g.material_id === it.material_id).reduce((s,g)=>s+(g.qty||0),0);
      const bal = Math.max(0, (it.qty||0) - recvd);
      return { ...it, received_before: recvd, balance: bal, receive_qty: bal };
    });
    setFromPO({ ...po, items: remaining, warehouse_id: "wh_rm" });
  };
  const qc = async (g, status) => {
    try { await api.patch(`/grn/${g.grn_id}/qc`, { status }); toast.success(`QC ${status}`); mutate("/grn"); }
    catch(e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="grn-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">GRN — Purchase Receipt</h1>
          <p className="text-sm text-slate-500">{(grns||[]).length} receipts · {openPOs.length} open POs awaiting material</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="btn-create-grn" onClick={()=>setShow(true)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:border-sky-400">+ Manual GRN</button>
          <button data-testid="btn-grn-from-po" onClick={()=>setFromPO("picker")} disabled={openPOs.length===0} className="bg-sky-600 text-white text-sm px-3 py-1.5 rounded-md flex items-center gap-1 disabled:opacity-50"><Truck size={12}/>Receive against PO</button>
        </div>
      </div>

      {openPOs.length>0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
          <div className="text-xs font-semibold uppercase text-sky-800 mb-2">Open Purchase Orders — click to receive</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {openPOs.map(po => (
              <button key={po.ppo_id} data-testid={`open-po-${po.ppo_no}`} onClick={()=>openFromPO(po)} className="text-left bg-white hover:border-sky-500 border border-slate-200 rounded p-2">
                <div className="font-mono text-xs text-slate-500">{po.ppo_no}</div>
                <div className="font-semibold text-sm">{sMap[po.supplier_id]?.name || po.supplier_id}</div>
                <div className="text-xs text-slate-500">{po.items.length} line(s) · ₹{(po.total||0).toLocaleString()} · <span className={`pill ${STATUS_TO_PILL(po.status)}`}>{po.status}</span></div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="grn-table">
          <thead><tr><th>GRN No</th><th>PO No</th><th>Supplier</th><th>Material</th><th>Batch</th><th>Qty</th><th>Rate</th><th>QC</th><th>Action</th></tr></thead>
          <tbody>
            {(grns||[]).map(g=>(
              <tr key={g.grn_id}>
                <td className="text-xs font-mono">{g.grn_no}</td>
                <td className="text-xs font-mono text-sky-600">{g.ppo_no || "-"}</td>
                <td>{sMap[g.supplier_id]?.name || "-"}</td>
                <td>{mMap[g.material_id]?.name || g.material_id}</td>
                <td className="text-xs font-mono">{g.batch}</td>
                <td className="font-mono">{g.qty}</td>
                <td className="font-mono">₹{g.rate}</td>
                <td><span className={`pill ${STATUS_TO_PILL(g.qc_status)}`}>{g.qc_status}</span></td>
                <td>{g.qc_status==="QUARANTINE" && <><button data-testid={`btn-grn-approve-${g.grn_id}`} onClick={()=>qc(g,"APPROVED")} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white mr-1">Approve</button><button data-testid={`btn-grn-reject-${g.grn_id}`} onClick={()=>qc(g,"REJECTED")} className="text-xs px-2 py-1 rounded bg-red-600 text-white">Reject</button></>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {fromPO === "picker" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={()=>setFromPO(null)}>
          <div className="bg-white rounded-xl w-full max-w-lg p-5" onClick={e=>e.stopPropagation()}>
            <h3 className="font-semibold mb-3">Pick a Purchase Order</h3>
            <div className="space-y-2 max-h-80 overflow-auto">
              {openPOs.map(po => (
                <button key={po.ppo_id} data-testid={`picker-po-${po.ppo_no}`} onClick={()=>openFromPO(po)} className="w-full text-left border border-slate-200 hover:border-sky-500 rounded p-2">
                  <div className="font-mono text-xs">{po.ppo_no}</div>
                  <div className="font-semibold text-sm">{sMap[po.supplier_id]?.name}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {fromPO && typeof fromPO === "object" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={()=>setFromPO(null)}>
          <div className="bg-white rounded-xl w-full max-w-3xl p-5 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="grn-from-po-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-slate-900">Receive against {fromPO.ppo_no}</h3>
                <div className="text-xs text-slate-500">Supplier: {sMap[fromPO.supplier_id]?.name} · Total PO ₹{(fromPO.total||0).toLocaleString()}</div>
              </div>
              <button onClick={()=>setFromPO(null)} className="text-slate-400">✕</button>
            </div>
            <div className="mb-3"><F label="Warehouse"><select data-testid="grn-po-warehouse" value={fromPO.warehouse_id} onChange={e=>setFromPO({...fromPO, warehouse_id:e.target.value})} className="i">{(warehouses||[]).filter(w=>["RAW_MATERIAL","PACKAGING"].includes(w.type)).map(w=><option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>)}</select></F></div>
            <table className="data-table">
              <thead><tr><th>Material</th><th>Ordered</th><th>Recv Before</th><th>Balance</th><th>Receiving Now</th><th>Batch</th><th>Rate</th></tr></thead>
              <tbody>
                {fromPO.items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{mMap[it.material_id]?.name || it.material_id}</td>
                    <td className="font-mono">{it.qty}</td>
                    <td className="font-mono">{it.received_before}</td>
                    <td className="font-mono">{it.balance}</td>
                    <td><input data-testid={`grn-po-qty-${idx}`} type="number" value={it.receive_qty} onChange={e=>{const items=[...fromPO.items]; items[idx].receive_qty=Number(e.target.value); setFromPO({...fromPO, items});}} className="i w-24"/></td>
                    <td><input data-testid={`grn-po-batch-${idx}`} value={it.batch||""} placeholder="auto" onChange={e=>{const items=[...fromPO.items]; items[idx].batch=e.target.value; setFromPO({...fromPO, items});}} className="i w-32"/></td>
                    <td className="font-mono">₹{it.rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setFromPO(null)} className="text-sm px-3 py-1.5 rounded border border-slate-200">Cancel</button>
              <button data-testid="btn-submit-grn-from-po" onClick={submitFromPO} className="text-sm px-4 py-1.5 rounded bg-sky-600 text-white">Post GRN(s)</button>
            </div>
          </div>
        </div>
      )}

      {show && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-xl p-5" data-testid="grn-modal">
            <h3 className="font-semibold text-slate-900 mb-4">Manual GRN</h3>
            <div className="grid grid-cols-2 gap-3">
              <F label="Supplier *"><select data-testid="grn-input-supplier" value={f.supplier_id||""} onChange={e=>setF({...f,supplier_id:e.target.value})} className="i"><option value="">Select</option>{(suppliers||[]).map(s=><option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}</select></F>
              <F label="Material *"><select data-testid="grn-input-material" value={f.material_id||""} onChange={e=>setF({...f,material_id:e.target.value})} className="i"><option value="">Select</option>{(materials||[]).map(m=><option key={m.material_id} value={m.material_id}>{m.name}</option>)}</select></F>
              <F label="Batch"><input data-testid="grn-input-batch" value={f.batch||""} onChange={e=>setF({...f,batch:e.target.value})} className="i" placeholder="auto"/></F>
              <F label="Warehouse"><select data-testid="grn-input-wh" value={f.warehouse_id} onChange={e=>setF({...f,warehouse_id:e.target.value})} className="i">{(warehouses||[]).map(w=><option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>)}</select></F>
              <F label="Qty *"><input data-testid="grn-input-qty" type="number" value={f.qty||""} onChange={e=>setF({...f,qty:Number(e.target.value)})} className="i"/></F>
              <F label="Rate"><input data-testid="grn-input-rate" type="number" value={f.rate||""} onChange={e=>setF({...f,rate:Number(e.target.value)})} className="i"/></F>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setShow(false)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button data-testid="btn-submit-grn" onClick={submit} className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white">Post GRN</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.i{width:100%;font-size:.85rem;border:1px solid #E2E8F0;border-radius:6px;padding:.4rem .5rem;background:#fff}`}</style>
    </div>
  );
}

export function FGInventoryPage() {
  const { data: fgs } = useSWR("/fg-inventory", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const pMap = Object.fromEntries((products||[]).map(p=>[p.product_id,p]));
  return (
    <div className="space-y-4" data-testid="fg-inventory-page">
      <h1 className="text-2xl font-bold text-slate-900">Finished Goods Inventory</h1>
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="fg-table">
          <thead><tr><th>Batch</th><th>Product</th><th>Qty</th><th>Available</th><th>Warehouse</th><th>Status</th><th>Mfg Date</th></tr></thead>
          <tbody>
            {(fgs||[]).map(f=>(
              <tr key={f.fg_id}>
                <td className="font-mono text-xs">{f.batch}</td>
                <td>{pMap[f.product_id]?.name || f.product_id}</td>
                <td className="font-mono">{f.qty?.toLocaleString()}</td>
                <td className="font-mono">{f.available_qty?.toLocaleString()}</td>
                <td>{f.warehouse_id}</td>
                <td><span className={`pill ${STATUS_TO_PILL(f.status)}`}>{f.status}</span></td>
                <td className="text-xs">{f.manufactured_at?.slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DispatchPage() {
  const { data: dispatches } = useSWR("/dispatch", fetcher);
  const { data: fgs } = useSWR("/fg-inventory", fetcher);
  const { data: customers } = useSWR("/customers", fetcher);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({});
  const availableFgs = (fgs||[]).filter(x=>x.available_qty>0 && x.status==="APPROVED");
  const submit = async () => {
    try { await api.post("/dispatch", f); toast.success("Dispatch created"); setShow(false); setF({}); mutate("/dispatch"); mutate("/fg-inventory"); }
    catch(e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="space-y-4" data-testid="dispatch-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Dispatch</h1>
        <button data-testid="btn-create-dispatch" onClick={()=>setShow(true)} className="bg-sky-600 text-white text-sm px-3 py-1.5 rounded-md">+ New Dispatch</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="dispatch-table">
          <thead><tr><th>DN No</th><th>Customer</th><th>Batch</th><th>Qty</th><th>Vehicle</th><th>Destination</th><th>Date</th></tr></thead>
          <tbody>{(dispatches||[]).map(d=>(<tr key={d.dispatch_id}><td className="text-xs font-mono">{d.dispatch_no}</td><td>{d.customer_id}</td><td className="text-xs font-mono">{d.batch}</td><td className="font-mono">{d.qty?.toLocaleString()}</td><td>{d.vehicle}</td><td>{d.destination}</td><td className="text-xs">{d.date?.slice(0,10)}</td></tr>))}</tbody>
        </table>
      </div>
      {show && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-xl p-5" data-testid="dispatch-modal">
            <h3 className="font-semibold text-slate-900 mb-4">Create Dispatch</h3>
            <div className="grid grid-cols-2 gap-3">
              <F label="Customer *"><select data-testid="disp-input-customer" value={f.customer_id||""} onChange={e=>setF({...f,customer_id:e.target.value})} className="input"><option value="">Select</option>{(customers||[]).map(c=><option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}</select></F>
              <F label="FG Batch *"><select data-testid="disp-input-fg" value={f.fg_id||""} onChange={e=>setF({...f,fg_id:e.target.value})} className="input"><option value="">Select</option>{availableFgs.map(x=><option key={x.fg_id} value={x.fg_id}>{x.batch} ({x.available_qty})</option>)}</select></F>
              <F label="Qty *"><input data-testid="disp-input-qty" type="number" value={f.qty||""} onChange={e=>setF({...f,qty:Number(e.target.value)})} className="input"/></F>
              <F label="Vehicle"><input value={f.vehicle||""} onChange={e=>setF({...f,vehicle:e.target.value})} className="input"/></F>
              <F label="Driver"><input value={f.driver||""} onChange={e=>setF({...f,driver:e.target.value})} className="input"/></F>
              <F label="Destination"><input value={f.destination||""} onChange={e=>setF({...f,destination:e.target.value})} className="input"/></F>
              <F label="Transporter"><input value={f.transporter||""} onChange={e=>setF({...f,transporter:e.target.value})} className="input"/></F>
              <F label="Invoice No"><input value={f.invoice_no||""} onChange={e=>setF({...f,invoice_no:e.target.value})} className="input"/></F>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setShow(false)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button data-testid="btn-submit-dispatch" onClick={submit} className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white">Create</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;font-size:.85rem;border:1px solid #E2E8F0;border-radius:6px;padding:.4rem .5rem;background:#fff}`}</style>
    </div>
  );
}

export function TraceabilityPage() {
  const [batch, setBatch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const trace = async () => {
    if (!batch) return;
    setLoading(true);
    try { const r = await api.get(`/traceability/${batch}`); setData(r.data); }
    catch(e){ toast.error("Not found"); setData(null); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-4" data-testid="traceability-page">
      <h1 className="text-2xl font-bold text-slate-900">Batch Traceability</h1>
      <div className="flex gap-2 max-w-xl">
        <input data-testid="trace-input" value={batch} onChange={e=>setBatch(e.target.value)} placeholder="Enter batch code e.g. FB-260101-02" className="flex-1 text-sm border border-slate-200 rounded px-3 py-2"/>
        <button data-testid="btn-trace" onClick={trace} className="bg-sky-600 text-white text-sm px-4 rounded">Trace</button>
      </div>
      {loading && <div className="text-slate-500 text-sm">Loading...</div>}
      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <TraceNode title="Finished Goods" data={data.fg}/>
          <TraceNode title="QC Batch" data={data.qc}/>
          <TraceNode title="Production Order" data={data.production_order}/>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-semibold text-slate-800 mb-2">Production Entries</div>
            <table className="data-table"><thead><tr><th>Time</th><th>Good</th><th>Rejected</th><th>Total</th></tr></thead><tbody>{(data.entries||[]).map(e=><tr key={e.entry_id}><td className="text-xs">{e.timestamp?.slice(0,16)}</td><td>{e.good_qty}</td><td>{e.rejected_qty}</td><td>{e.total}</td></tr>)}</tbody></table>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 md:col-span-2">
            <div className="font-semibold text-slate-800 mb-2">Dispatches</div>
            <table className="data-table"><thead><tr><th>DN</th><th>Customer</th><th>Qty</th><th>Vehicle</th><th>Date</th></tr></thead><tbody>{(data.dispatches||[]).map(d=><tr key={d.dispatch_id}><td className="text-xs font-mono">{d.dispatch_no}</td><td>{d.customer_id}</td><td>{d.qty}</td><td>{d.vehicle}</td><td>{d.date?.slice(0,10)}</td></tr>)}</tbody></table>
          </div>
        </div>
      )}
    </div>
  );
}

const TraceNode = ({title, data}) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="font-semibold text-slate-800 mb-2">{title}</div>
    {data ? (
      <div className="text-xs space-y-1">
        {Object.entries(data).slice(0,10).map(([k,v])=>(
          <div key={k} className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">{k}</span><span className="font-mono text-slate-800 truncate ml-2">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>
        ))}
      </div>
    ) : <div className="text-slate-400 text-sm italic">No record</div>}
  </div>
);
